/* eslint-disable jsx-a11y/iframe-has-title */
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { ref, onValue, off, set } from "firebase/database";
import { database } from "../constants/firebase";
import { ICONS } from "../constants/icons";
import useMoveTo from "../constants/navigation";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import GuestHeader from "../components/GuestHeader";
import { useUser } from "../contexts/UserContext";
import RatingStars from "../components/RatingStars";
import { ROUTES } from "../App";
import MobileHeader from "../components/MobileHeader";
import ProductCheckout from "../components/ProductCheckout";
import BunnyVideoEmbed from "../components/BunnyVideoEmbed";
import { copyToClipboard, formatPrice } from "../utils/utils";
import useExchangeRates from "../hooks/useExchangeRates";
import { HiArrowLeft } from "react-icons/hi";
import Testimonials from "../components/Testimonials";
import SubmitTestimonialModal from "../components/SubmitTestimonialModal";
import ManageTestimonialsModal from "../components/ManageTestimonialsModal";
import { FiMessageSquare, FiSettings } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";

const ProductDetails = () => {
    const navigate = useMoveTo();
    const params = useParams();
    const productId = params.productId;
    const { currentUser } = useUser();
    const isInApp = window.isInApp;
    const location = useLocation();
    const fromLibrary = location.state?.fromLibrary || false;
    const isPreview = location.pathname.includes("/preview/");

    const [product, setProduct] = useState(null);
    const [creator, setCreator] = useState("Unknown");
    const [creatorID, setCreatorID] = useState("");
    const [creatorData, setCreatorData] = useState(null);
    const [userRating, setUserRating] = useState(0);
    const [hasRated, setHasRated] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [showCheckout, setShowCheckout] = useState(false);
    const [hasPurchased, setHasPurchased] = useState(false);
    const [sellerHasPayoutMethod, setSellerHasPayoutMethod] = useState(true);

    // Add new state for currency conversion
    const [displayPrice, setDisplayPrice] = useState(null);
    const [displayCurrency, setDisplayCurrency] = useState("");
    const [userCountry, setUserCountry] = useState(""); // Will store user's country
    const { exchangeRates, convertPrice } = useExchangeRates();
    // Bookmarks state (Option B: full object)
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [addingBookmark, setAddingBookmark] = useState(false);
    const [deletingBookmark, setDeletingBookmark] = useState(false);

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [customBusinessName, setCustomBusinessName] = useState("");
    const [existingShare, setExistingShare] = useState(null);
    const isCreator = currentUser?.uid && product?.userId === currentUser.uid;

    const [showTrailerModal, setShowTrailerModal] = useState(false);
    const [isTrailerLoading, setIsTrailerLoading] = useState(false);

    const [showSubmitTestimonial, setShowSubmitTestimonial] = useState(false);
    const [showManageTestimonials, setShowManageTestimonials] = useState(false);

    const requireAuth = (action) => {
        if (!currentUser?.uid) {
            navigate(`${ROUTES.SIGN_IN}?redirect=/product/${productId}`);
            return false;
        }
        return true;
    };

    useEffect(() => {
        if (!currentUser?.uid || !productId) return;

        const shareRef = ref(database, `SharedLinks/${productId}/${currentUser.uid}`);

        const unsubscribe = onValue(shareRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                setExistingShare(data);
                setCustomBusinessName(data.businessName || "");
            } else {
                setExistingShare(null);
            }
        });

        return () => off(shareRef, "value", unsubscribe);
    }, [currentUser?.uid, productId]);

    // ✅ Determine user's country
    useEffect(() => {
        const determineUserCountry = () => {
            // Method 1: From user profile if available
            if (currentUser?.country) {
                setUserCountry(currentUser.country);
                return currentUser.country;
            }
        };

        determineUserCountry();
    }, [currentUser]);

    // ✅ Calculate display price based on user country
    useEffect(() => {
        if (!product?.price) return;

        const price = parseFloat(product.price);
        const productCurrency = (product.currency || "").trim().toUpperCase();

        let calculatedPrice = price;
        let calculatedCurrency = productCurrency;

        // If user is in South Africa, show ZAR
        if (userCountry === "South Africa") {
            if (productCurrency === "USD") {
                calculatedPrice = convertPrice(price, "USD", "ZAR");
                calculatedCurrency = "ZAR";
            }
            // If product is already in ZAR, keep as is
            else if (productCurrency === "ZAR") {
                calculatedPrice = price;
                calculatedCurrency = "ZAR";
            }
        }
        // If user is International (not South Africa), show USD
        else {
            if (productCurrency === "ZAR") {
                calculatedPrice = convertPrice(price, "ZAR", "USD");
                calculatedCurrency = "USD";
            }
            // If product is already in USD, keep as is
            else if (productCurrency === "USD") {
                calculatedPrice = price;
                calculatedCurrency = "USD";
            }
        }

        setDisplayPrice(calculatedPrice);
        setDisplayCurrency(calculatedCurrency);
    }, [product, userCountry, exchangeRates, convertPrice]);


    // ✅ Handle purchase status from URL params
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const purchaseStatus = params.get('purchase');

        if (purchaseStatus === 'success') {
            window.showToast("Purchase successful! Product added to your library.", "success");
            // Clean URL
            window.history.replaceState({}, '', `${window.location.pathname}${window.location.hash.split('?')[0]}`);
        } else if (purchaseStatus === 'cancelled') {
            window.showToast("Purchase cancelled.", "info");
            // Clean URL
            window.history.replaceState({}, '', `${window.location.pathname}${window.location.hash.split('?')[0]}`);
        }
    }, [location.search]);

    // ✅ Load product by ID
    useEffect(() => {
        const productRef = ref(database, `Listings/${productId}`);
        const unsubscribe = onValue(productRef, (snapshot) => {
            if (snapshot.exists()) {
                setProduct({ id: productId, ...snapshot.val() });
            }
        });

        return () => off(productRef, "value", unsubscribe);
    }, [productId]);

    // ✅ Load creator name and check payout method
    useEffect(() => {
        if (product?.userId) {
            const userRef = ref(database, `Users/${product.userId}`);
            const unsubscribe = onValue(userRef, (snapshot) => {
                if (snapshot.exists()) {
                    const userData = snapshot.val();
                    setCreator(userData.name || "Unknown");
                    setCreatorID(userData.uid || "");
                    setCreatorData(userData);

                    // Check if seller has valid payout method
                    const hasStripe = !!userData.stripeConnectedAccountId;
                    const hasPaystack = !!userData.paystackSubaccountCode;
                    setSellerHasPayoutMethod(hasStripe || hasPaystack);
                }
            });

            return () => off(userRef, "value", unsubscribe);
        }
    }, [product?.userId]);

    // ✅ Load user rating if exists
    useEffect(() => {
        if (!currentUser?.uid || !productId) return;
        const ratingRef = ref(database, `Listings/${productId}/ratings/${currentUser.uid}`);

        const unsubscribe = onValue(ratingRef, (snapshot) => {
            if (snapshot.exists()) {
                setUserRating(snapshot.val().rating);
                setHasRated(true);
            }
        });

        return () => off(ratingRef, "value", unsubscribe);
    }, [currentUser?.uid, productId]);

    // ✅ Handle submit rating
    const handleSubmitRating = async (value) => {
        if (!requireAuth("rating")) return;
        if (!currentUser?.uid) {
            window.showToast("Please login to submit rating.", "error");
            return;
        }

        try {
            const ratingRef = ref(database, `Listings/${productId}/ratings/${currentUser.uid}`);
            await set(ratingRef, {
                rating: value,
                createdAt: new Date().toISOString(),
            });

            setUserRating(value);
            setHasRated(true);
            window.showToast("Thank you for rating!", "success");
        } catch (error) {
            console.error("Error saving rating:", error);
            window.showToast("Failed to submit rating. Try again.", "error");
        }
    };

    // ✅ Check if product/course is already in user's library
    useEffect(() => {
        if (!currentUser?.uid) return;

        const libRef = ref(database, `Users/${currentUser.uid}/library/${productId}`);
        const unsubscribe = onValue(libRef, (snapshot) => {
            const exists = snapshot.exists();

            // For both products and courses, check if purchased (has purchase data)
            if (exists) {
                const data = snapshot.val();
                setHasPurchased(!!data.purchasePrice || !!data.totalPaid); // Has purchase data
            } else {
                setHasPurchased(false);
            }
        });

        return () => off(libRef, "value", unsubscribe);
    }, [currentUser?.uid, productId]);

    // ✅ Load bookmark status (Option B)
    useEffect(() => {
        if (!currentUser?.uid) {
            setIsBookmarked(false);
            return;
        }

        const bookmarkRef = ref(database, `Users/${currentUser.uid}/bookmarks/${productId}`);
        const unsubscribe = onValue(bookmarkRef, (snapshot) => {
            setIsBookmarked(snapshot.exists());
        });

        return () => off(bookmarkRef, "value", unsubscribe);
    }, [currentUser?.uid, productId]);


    // ✅ Bookmark handlers (Option B: full object)
    const handleAddBookmark = async () => {
        if (!requireAuth("bookmarks")) return;
        if (!currentUser?.uid) {
            window.showToast("Please login first", "error");
            return;
        }
        try {
            setAddingBookmark(true);
            const bookmarkRef = ref(database, `Users/${currentUser.uid}/bookmarks/${productId}`);
            await set(bookmarkRef, {
                productId,
                addedAt: new Date().toISOString(),
                title: product?.title || "",
                coverImageUrl: product?.coverImageUrl || "",
                type: product?.type || ""
            });
            window.showToast("Added to bookmarks", "success");
        } catch (e) {
            console.error(e);
            window.showToast("Failed to bookmark", "error");
        } finally {
            setAddingBookmark(false);
        }
    };

    const handleRemoveBookmark = async () => {
        if (!requireAuth("bookmarks")) return;
        if (!currentUser?.uid) return;
        try {
            setDeletingBookmark(true);
            const bookmarkRef = ref(database, `Users/${currentUser.uid}/bookmarks/${productId}`);
            await set(bookmarkRef, null);
            window.showToast("Removed from bookmarks", "success");
        } catch (e) {
            console.error(e);
            window.showToast("Failed to remove bookmark", "error");
        } finally {
            setDeletingBookmark(false);
        }
    };

    const handleDownloadFile = async () => {
        if (!product) return;

        setIsDownloading(true);
        try {
            const response = await fetch(product.fileUrl, { mode: "cors" });
            const blob = await response.blob();

            // Extract file name from Firebase URL (after `products%2Ffiles%2F`)
            let fileName = decodeURIComponent(
                product.fileUrl.split("/o/")[1].split("?")[0].split("%2F").pop()
            );

            if (product.title) {
                const lastDotIndex = fileName.lastIndexOf(".");
                const ext = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : "";
                fileName = `${product.title}${ext}`;
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName || "file";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed:", error);
            window.showToast("Download failed. Please try again.", "error");
        } finally {
            setIsDownloading(false);
        }
    };


    const handleSaveShareLink = () => {
        if (!requireAuth("share-link")) return;
        if (!currentUser?.uid) {
            window.showToast("Please login first", "error");
            return;
        }

        if (!customBusinessName.trim()) {
            window.showToast("Enter your business name", "error");
            return;
        }

        const slug = customBusinessName
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^a-z0-9-]/g, "");

        const shareLink = `${window.location.origin}/#/preview/${slug}/${productId}`;

        // ✅ Copy immediately inside user click (iOS-safe)
        copyToClipboard(shareLink)
            .then(() => {
                window.showToast(
                    existingShare
                        ? "Link updated & copied!"
                        : "Link created & copied!",
                    "success"
                );
            })
            .catch((err) => {
                console.error("Clipboard error:", err);
                window.showToast("Failed to copy link", "error");
            });

        // 🔥 Async DB write in background
        (async () => {
            try {
                await set(
                    ref(database, `SharedLinks/${productId}/${currentUser.uid}`),
                    {
                        ownerId: currentUser.uid,
                        productId,
                        slug,
                        businessName: customBusinessName,
                        createdAt: existingShare?.createdAt || new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                    }
                );
            } catch (error) {
                console.error("DB save error:", error);
                // Notify the user that the link might not work
                window.showToast("Failed to save link to database", "error");
            }
        })();

        setIsMenuOpen(false);
    };

    if (!product) {
        return (
            <div className="flex items-center justify-center h-screen text-gray-500">
                Loading product details...
            </div>
        );
    }

    // Derived flags
    const isPaid = parseFloat(product.price || 0) > 0;


    const handleGoBack = () => {
        // Try to go back, if there's no history, go to Chats
        if (window.history.length > 2) { // >2 because typically there's at least the current page and the previous
            navigate(-1);
        } else {
            navigate(ROUTES.DASHBOARD);
        }
    };

    const handleOpenTrailerModal = () => {
        setShowTrailerModal(true);
        setIsTrailerLoading(true);
    };

    const handleCloseTrailerModal = () => {
        setShowTrailerModal(false);
        setIsTrailerLoading(false);
    };

    return (
        <div className="h-screen flex flex-col bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-y-auto">
            {/* Mobile Header (Always show on mobile) */}
            <MobileHeader 
                title={fromLibrary ? "Course Cover Page" : "Product Details"} 
                isBackBtn={currentUser || !isPreview} 
                handleBack={handleGoBack} 
                rightElement={
                    !currentUser ? (
                        <button
                            onClick={() => navigate(ROUTES.SIGN_IN)}
                            className="text-xs bg-[#327691] text-white px-3 py-1 rounded-md font-semibold"
                        >
                            Log In
                        </button>
                    ) : (
                        isCreator ? (
                            <div className="relative">
                                <button
                                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                                    className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                >
                                    <svg width="20" height="20" fill="currentColor">
                                        <circle cx="5" cy="10" r="2" />
                                        <circle cx="10" cy="10" r="2" />
                                        <circle cx="15" cy="10" r="2" />
                                    </svg>
                                </button>

                                {isMenuOpen && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-[#1F1F1F] shadow-lg rounded-xl border border-gray-200 dark:border-gray-700 p-4 z-50">

                                        <p className="text-sm font-semibold mb-2">
                                            {existingShare ? "Edit Share Link" : "Create Share Link"}
                                        </p>

                                        <input
                                            type="text"
                                            placeholder="Enter business name"
                                            value={customBusinessName}
                                            onChange={(e) => setCustomBusinessName(e.target.value)}
                                            className="w-full rounded-lg px-4 py-1.5 outline-none placeholder:text-[#7C8BA0] mb-3
                       bg-[#F5F9FE] dark:bg-white 
                       text-black dark:text-black
                       border border-gray-200 dark:border-0"
                                        />

                                        {existingShare && (
                                            <div className="text-xs text-gray-500 mb-2 break-all">
                                                Current Link:
                                                <br />
                                                {`${window.location.origin}/#/preview/${existingShare.slug}/${productId}`}
                                            </div>
                                        )}

                                        <button
                                            onClick={handleSaveShareLink}
                                            className="w-full bg-[#327691] text-white py-2 rounded-lg text-sm mb-2"
                                        >
                                            {existingShare ? "Update Link" : "Generate Link"}
                                        </button>

                                        {product.type === "course" && (
                                            <button
                                                onClick={() => {
                                                    setIsMenuOpen(false);
                                                    setShowManageTestimonials(true);
                                                }}
                                                className="w-full bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white py-2 rounded-lg text-sm flex items-center justify-center gap-2"
                                            >
                                                <FiMessageSquare className="w-4 h-4" />
                                                Manage Testimonials
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : null
                    )
                } 
            />

            <div className="flex flex-1 overflow-hidden">
                {!isPreview || currentUser ? <Sidebar /> : null}
                
                <main className="flex-1 flex flex-col">
                    {isPreview && !currentUser && <GuestHeader />}

                    {/* Top Section (fixed inside main, not scrolling) */}
                    <div className="flex-shrink-0 pt-5 md:pt-10 px-6">
                        <div className="flex items-center justify-between">
                            {/* Title & Back Button */}
                            <div className="flex items-center gap-4 mb-8">
                                {(currentUser || !isPreview) && (
                                    <button
                                        onClick={handleGoBack}
                                        className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-colors hidden md:block"
                                    >
                                        <HiArrowLeft className="w-6 h-6" />
                                    </button>
                                )}
                                <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">
                                    {fromLibrary ? "Course Cover Page" : "Product Details"}
                                </h2>
                            </div>
                            
                            {isCreator ? (
                                <div className="relative md:block hidden">
                                    <button
                                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                                        className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                    >
                                        <svg width="20" height="20" fill="currentColor">
                                            <circle cx="5" cy="10" r="2" />
                                            <circle cx="10" cy="10" r="2" />
                                            <circle cx="15" cy="10" r="2" />
                                        </svg>
                                    </button>

                                    {isMenuOpen && (
                                        <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-[#1F1F1F] shadow-lg rounded-xl border border-gray-200 dark:border-gray-700 p-4 z-50">

                                            <p className="text-sm font-semibold mb-2">
                                                {existingShare ? "Edit Share Link" : "Create Share Link"}
                                            </p>

                                            <input
                                                type="text"
                                                placeholder="Enter business name"
                                                value={customBusinessName}
                                                onChange={(e) => setCustomBusinessName(e.target.value)}
                                                className="w-full rounded-lg px-4 py-1.5 outline-none placeholder:text-[#7C8BA0] mb-3
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0"
                                            />

                                            {existingShare && (
                                                <div className="text-xs text-gray-500 mb-2 break-all">
                                                    Current Link:
                                                    <br />
                                                    {`${window.location.origin}/#/preview/${existingShare.slug}/${productId}`}
                                                </div>
                                            )}

                                            <button
                                                onClick={handleSaveShareLink}
                                                className="w-full bg-[#327691] text-white py-2 rounded-lg text-sm mb-2"
                                            >
                                                {existingShare ? "Update Link" : "Generate Link"}
                                            </button>

                                            {product.type === "course" && (
                                                <button
                                                    onClick={() => {
                                                        setIsMenuOpen(false);
                                                        setShowManageTestimonials(true);
                                                    }}
                                                    className="w-full bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white py-2 rounded-lg text-sm flex items-center justify-center gap-2"
                                                >
                                                    <FiMessageSquare className="w-4 h-4" />
                                                    Manage Testimonials
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) :
                                <div className="" />
                            }
                        </div>
                    </div>

                    {/* Scrollable Middle Section */}
                    <div className="flex-1 overflow-y-auto p-6 pb-16 md:pb-6">
                        <div className="flex flex-col md:flex-row gap-6">
                            {/* Left Image */}
                            <div className="md:w-1/3">
                                <img
                                    src={product.coverImageUrl || IMAGES.courses}
                                    alt="Product"
                                    className="rounded-xl w-full h-96 md:h-[600px] object-cover"
                                />
                            </div>

                            {/* Right Content */}
                            <div className="flex-1">
                                <h1 className="text-3xl font-bold">{product.title}</h1>
                                <p className="text-gray-500 dark:text-white/50 mt-1 flex items-center gap-2">
                                    Created by:{" "}
                                    <span
                                        className={`flex items-center gap-1 font-semibold cursor-pointer ${creatorData?.role === "Host"
                                            ? "dark:glow-text-dark glow-text-light"
                                            : "text-gray-500 dark:text-white/50"
                                            }`}
                                        onClick={() => {
                                            if (creatorID) {
                                                navigate(`${ROUTES.VIEW_PROFILE}/${creatorID}`);
                                            } else {
                                                window.showToast("Creator profile not available.", "error");
                                            }
                                        }}
                                    >
                                        {creator}
                                        {creatorData?.isTeamUser && (
                                            <svg width="16px" height="16px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#327691">
                                                <path d="M10.5213 2.62368C11.3147 1.75255 12.6853 1.75255 13.4787 2.62368L14.4989 3.74391C14.8998 4.18418 15.4761 4.42288 16.071 4.39508L17.5845 4.32435C18.7614 4.26934 19.7307 5.23857 19.6757 6.41554L19.6049 7.92905C19.5771 8.52388 19.8158 9.10016 20.2561 9.50111L21.3763 10.5213C22.2475 11.3147 22.2475 12.6853 21.3763 13.4787L20.2561 14.4989C19.8158 14.8998 19.5771 15.4761 19.6049 16.071L19.6757 17.5845C19.7307 18.7614 18.7614 19.7307 17.5845 19.6757L16.071 19.6049C15.4761 19.5771 14.8998 19.8158 14.4989 20.2561L13.4787 21.3763C12.6853 22.2475 11.3147 22.2475 10.5213 21.3763L9.50111 20.2561C9.10016 19.8158 8.52388 19.5771 7.92905 19.6049L6.41553 19.6757C5.23857 19.7307 4.26934 18.7614 4.32435 17.5845L4.39508 16.071C4.42288 15.4761 4.18418 14.8998 3.74391 14.4989L2.62368 13.4787C1.75255 12.6853 1.75255 11.3147 2.62368 10.5213L3.74391 9.50111C4.18418 9.10016 4.42288 8.52388 4.39508 7.92905L4.32435 6.41553C4.26934 5.23857 5.23857 4.26934 6.41554 4.32435L7.92905 4.39508C8.52388 4.42288 9.10016 4.18418 9.50111 3.74391L10.5213 2.62368Z" stroke="#327691" strokeWidth="1.5"></path>
                                                <path d="M9 12L11 14L15 10" stroke="#327691" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                                            </svg>
                                        )}
                                    </span>
                                </p>
                                {isPaid && (
                                    <div className="mt-3 flex items-center gap-2">
                                        <span className="text-2xl font-bold text-[#327691]">
                                            {displayPrice !== null ? formatPrice(displayPrice, displayCurrency, product.currency) : "Loading..."}
                                        </span>
                                        {userCountry && (
                                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                                (Price for {userCountry === "South Africa" ? "South Africa" : "International"})
                                            </span>
                                        )}
                                        {product.currency && displayCurrency && product.currency.toUpperCase() !== displayCurrency && (
                                            <span className="text-xs text-gray-400">
                                                Converted from {product.currency.toUpperCase()}
                                            </span>
                                        )}
                                    </div>
                                )}
                                <h3 className="mt-3 text-xl font-semibold border-t border-gray-300 dark:border-[#1D1F24] pt-2">
                                    Description
                                </h3>
                                <p className="mt-3 dark:text-gray-300 text-gray-500 leading-relaxed whitespace-pre-line">
                                    {product.explainer || "No description available."}
                                </p>

                                {/* Testimonials Section */}
                                {product.type === "course" && (
                                    <div className="mt-10 border-t border-gray-300 dark:border-[#1D1F24] pt-8">
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                                            <div className="flex-1">
                                                <Testimonials productId={productId} isCreator={isCreator} />
                                            </div>
                                        </div>
                                        
                                        {hasPurchased && !isCreator && (
                                            <div className="mt-8 p-6 rounded-3xl bg-[#327691]/5 border border-dashed border-[#327691]/30 flex flex-col items-center text-center">
                                                <div className="w-12 h-12 rounded-full bg-[#327691]/10 flex items-center justify-center text-[#327691] mb-3">
                                                    <FiMessageSquare className="w-6 h-6" />
                                                </div>
                                                <h4 className="font-bold text-lg dark:text-white">How was your experience?</h4>
                                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-sm">
                                                    Share your feedback and results with the community. Your testimonial helps others decide!
                                                </p>
                                                <button 
                                                    onClick={() => setShowSubmitTestimonial(true)}
                                                    className="px-6 py-2.5 rounded-xl bg-[#327691] text-white font-bold hover:opacity-90 transition-opacity"
                                                >
                                                    Share your experience
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Fixed Bottom Section - unified and cleaned (handles course/product/other) */}
                    <div className="w-full border-t border-gray-300 dark:border-[#1D1F24] bg-white dark:bg-[#1A1A1A] dark:text-white">
                        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
                            {/* Determine if left section will render */}
                            {(() => {
                                const showLeftSection =
                                    (product.type === "course" && (hasPurchased || !isPaid)) ||
                                    (product.type === "product" && hasPurchased) ||
                                    (hasPurchased || !isPaid);

                                const showRightSection = !hasPurchased && isPaid;

                                const justifyClass =
                                    showLeftSection && showRightSection
                                        ? "lg:justify-between"
                                        : "lg:justify-center";

                                return (
                                    <div className={`flex flex-col lg:flex-row lg:items-center gap-6 ${justifyClass}`}>
                                        {/* ================= LEFT SECTION ================= */}
                                        {showLeftSection && (
                                            <div className="flex flex-col md:flex-row items-center gap-4 w-full lg:w-auto">
                                                {/* Open Course */}
                                                {product.type === "course" && (hasPurchased || !isPaid) && (
                                                    <button
                                                        onClick={() => {
                                                            if (!requireAuth("open-course")) return;
                                                            navigate(`${ROUTES.CHAPTERS}/${productId}`);
                                                        }}
                                                        className="w-full md:w-[260px] px-6 py-3 rounded-xl bg-[#327691] hover:opacity-90 text-white font-semibold shadow-md"
                                                    >
                                                        Open Course
                                                    </button>
                                                )}

                                                {/* Download Product */}
                                                {product.type === "product" && hasPurchased && (
                                                    <button
                                                        disabled={isDownloading}
                                                        onClick={handleDownloadFile}
                                                        className={`w-full md:w-[260px] px-6 py-3 rounded-xl font-semibold transition 
                    ${isDownloading ? "bg-gray-400 cursor-not-allowed" : "bg-[#327691] hover:opacity-90"} text-white`}
                                                    >
                                                        {isDownloading ? "Downloading..." : "Download File"}
                                                    </button>
                                                )}

                                                {/* Rating Box */}
                                                {(hasPurchased || !isPaid) && (
                                                    <div className="flex items-center gap-4 w-full md:w-auto h-[52px] px-4 rounded-xl bg-[#3276918F] text-white justify-center">
                                                        <RatingStars
                                                            maxStars={5}
                                                            initialRating={userRating}
                                                            disabled={hasRated}
                                                            onSubmit={handleSubmitRating}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}



                                        {/* ================= RIGHT SECTION ================= */}
                                        <div className="flex flex-col md:flex-row items-center gap-4 w-full lg:w-auto">
                                            {showRightSection && (
                                                <>
                                                    {/* Seller has no payout method */}
                                                    {!sellerHasPayoutMethod ? (
                                                        <div className="md:flex-1 w-full">
                                                            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 w-full lg:w-auto flex flex-col items-center text-center">
                                                                <p className="text-sm text-yellow-800 dark:text-yellow-300 font-semibold">
                                                                    ⚠️ {product.type === "course" ? "Course" : "Product"} Not Available for Purchase
                                                                </p>
                                                                <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                                                                    The {product.type === "course" ? "course creator" : "seller"} hasn't completed their payout setup yet.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ) : isInApp ? (
                                                        /* In App message */
                                                        <div className="w-full flex flex-col items-center gap-3 text-center">
                                                            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 w-full">
                                                                <p className="text-sm text-yellow-800 dark:text-yellow-300 font-semibold">
                                                                    ⚠️ You cannot buy this product on the app.
                                                                </p>
                                                                <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                                                                    We know it's not ideal — please open this page in your browser to complete the purchase.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        // ) : !canShowPurchaseButton ? (
                                                        //     /* Warning only for app users when seller is Paystack */
                                                        //     <div className="w-full flex flex-col items-center gap-3 text-center">
                                                        //         <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 w-full">
                                                        //             <p className="text-sm text-yellow-800 dark:text-yellow-300 font-semibold">
                                                        //                 ⚠️ You cannot buy this product on the app.
                                                        //             </p>
                                                        //             <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                                                        //                 Purchases are only allowed for Stripe payouts in app. Please use the web browser.
                                                        //             </p>
                                                        //         </div>
                                                        //     </div>
                                                        // ) : (
                                                        /* Purchase button */
                                                        <button
                                                            onClick={() => {
                                                                if (!requireAuth("purchase")) return;
                                                                setShowCheckout(true);
                                                            }}
                                                            className={`
                                                                w-full md:w-[280px] px-8 py-3 rounded-xl font-semibold
                                                                 transition bg-[#327691] hover:opacity-90 text-white
                                                                `}
                                                        >

                                                            {`Purchase ${displayPrice !== null
                                                                ? formatPrice(displayPrice, displayCurrency)
                                                                : ""
                                                                }`}
                                                        </button>
                                                        //                                                 <button
                                                        //                                                     disabled={loading}
                                                        //                                                     onClick={() => {
                                                        //                                                         if (!requireAuth("purchase")) return;
                                                        //                                                         setShowCheckout(true);
                                                        //                                                     }}
                                                        //                                                     className={`w-full md:w-[280px] px-8 py-3 rounded-xl font-semibold transition
                                                        //   ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-[#327691] hover:opacity-90"} text-white`}
                                                        //                                                 >
                                                        //                                                     {loading
                                                        //                                                         ? "Processing..."
                                                        //                                                         : `Purchase ${displayPrice !== null
                                                        //                                                             ? formatPrice(displayPrice, displayCurrency)
                                                        //                                                             : ""
                                                        //                                                         }`}
                                                        //                                                 </button>
                                                        // ) : isInApp ? (
                                                        //     /* In App message */
                                                        //     <div className="w-full flex flex-col items-center gap-3 text-center">
                                                        //         <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 w-full">
                                                        //             <p className="text-sm text-yellow-800 dark:text-yellow-300 font-semibold">
                                                        //                 ⚠️ You cannot buy this product on the app.
                                                        //             </p>
                                                        //             <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                                                        //                 We know it's not ideal — please open this page in your browser to complete the purchase.
                                                        //             </p>
                                                        //         </div>
                                                        //     </div>
                                                        // ) : (
                                                    )}

                                                    {/* Trailer button */}
                                                    {product.type === "course" && product.trailerUrl && (
                                                        <button
                                                            disabled={isTrailerLoading}
                                                            onClick={handleOpenTrailerModal}
                                                            className={`w-full md:w-[280px] px-8 py-3 rounded-xl font-semibold transition 
          ${isTrailerLoading ? "bg-gray-400 cursor-not-allowed" : "bg-[#327691] hover:opacity-90"} text-white`}
                                                        >
                                                            Play Trailer
                                                        </button>
                                                    )}
                                                </>
                                            )}

                                            {/* Bookmark */}
                                            <div className="w-full md:w-[240px] mb-[4.3rem] md:mb-0">
                                                {isBookmarked ? (
                                                    <button
                                                        disabled={deletingBookmark}
                                                        onClick={handleRemoveBookmark}
                                                        className="w-full px-6 py-3 rounded-xl bg-red-500 hover:opacity-90 text-white font-semibold"
                                                    >
                                                        {deletingBookmark ? "Removing..." : "Remove Bookmark"}
                                                    </button>
                                                ) : (
                                                    <button
                                                        disabled={addingBookmark}
                                                        onClick={handleAddBookmark}
                                                        className="w-full px-6 py-3 rounded-xl bg-gray-600 hover:opacity-90 text-white font-semibold"
                                                    >
                                                        {addingBookmark ? "Adding..." : "Add to Bookmarks"}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </main>
            </div>

            {showTrailerModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
                    <div className="relative mx-4 flex max-h-[90vh] w-full max-w-5xl items-center justify-center rounded-xl bg-black p-4 md:mx-6 md:p-6">
                        {/* Close Button */}
                        <button
                            onClick={handleCloseTrailerModal}
                            className="absolute top-3 right-3 text-white text-xl font-bold z-50"
                        >
                            &times;
                        </button>

                        {/* Video iframe */}
                        <BunnyVideoEmbed
                            videoUrl={product.trailerUrl}
                            orientation={product.trailerOrientation}
                            videoWidth={product.trailerWidth}
                            videoHeight={product.trailerHeight}
                            variant="modal"
                            isLoading={isTrailerLoading}
                            onLoad={() => setIsTrailerLoading(false)}
                            wrapperClassName="shadow-2xl"
                            iframeClassName={isTrailerLoading ? "opacity-0 transition-opacity duration-300" : "opacity-100 transition-opacity duration-300"}
                            loadingOverlayClassName="absolute inset-0 flex items-center justify-center bg-black animate-pulse"
                            loadingIndicator={<p className="text-white">Loading trailer...</p>}
                            title={`${product.title || "Course"} trailer`}
                        />
                    </div>
                </div>
            )}

            {/* Product Checkout Modal */}
            {currentUser && (
                <ProductCheckout
                    product={product}
                    isOpen={showCheckout}
                    onClose={() => setShowCheckout(false)}
                    displayPrice={displayPrice}
                    displayCurrency={displayCurrency}
                    userCountry={userCountry}
                />
            )}

            {/* Testimonials Modals */}
            <SubmitTestimonialModal 
                isOpen={showSubmitTestimonial}
                onClose={() => setShowSubmitTestimonial(false)}
                productId={productId}
                courseTitle={product.title}
            />

            <ManageTestimonialsModal 
                isOpen={showManageTestimonials}
                onClose={() => setShowManageTestimonials(false)}
                productId={productId}
            />
        </div>
    );
};

export default ProductDetails;
